import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { API_URL, api, setOnSessionExpired } from "../utils/api";

interface AppContextType {
  lang: string;
  setLang: (l: string) => void;
  token: string | null;
  setToken: (t: string | null, refreshToken?: string | null) => void;
  user: any;
  setUser: (u: any) => void;
  // Menu latéral profil (SideMenu.tsx, monté une fois dans Layout.tsx) — partagé via le contexte
  // pour qu'un écran quelconque (ex: le lien "Gérer mes identités" de PublishAsSheet) puisse
  // l'ouvrir sans avoir à faire remonter un état local à travers l'arborescence de routes.
  sideMenuOpen: boolean;
  setSideMenuOpen: (open: boolean) => void;
  // Incrémenté par Réglages après un changement de partage de position — force l'effet de push
  // de position à repartir immédiatement (re-fetch du mode, push GPS instantané) sans attendre
  // le prochain rafraîchissement de token (~10 min), qui laissait une position périmée / aucun
  // point sur la carte des amis entre-temps.
  bumpLocationSharing: () => void;
}

const AppContext = createContext<AppContextType>({
  lang: "fr", setLang: () => {},
  token: null, setToken: () => {},
  user: null, setUser: () => {},
  sideMenuOpen: false, setSideMenuOpen: () => {},
  bumpLocationSharing: () => {},
});

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const [lang, setLang] = useState(localStorage.getItem("oltodo_lang") || "fr");
  const [token, setToken] = useState(localStorage.getItem("oltodo_token"));
  const [user, setUser] = useState<any>(JSON.parse(localStorage.getItem("oltodo_user") || "null"));
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [locationSharingTick, setLocationSharingTick] = useState(0);
  const refreshTokenRef = useRef<string | null>(localStorage.getItem("oltodo_refresh_token"));

  // Une requête individuelle (utils/api.ts#authFetch) peut découvrir la session morte entre deux
  // passages du rafraîchissement périodique ci-dessous (jusqu'à 10 min d'écart) — sans ce pont,
  // le localStorage était déjà nettoyé mais token/user restaient périmés en mémoire le temps que
  // le prochain tick périodique s'en rende compte à son tour, laissant l'appli affichée comme
  // connectée avec des listes vides pendant tout ce délai (voir commentaire juste en dessous).
  useEffect(() => {
    setOnSessionExpired(() => {
      refreshTokenRef.current = null;
      setToken(null);
      setUser(null);
    });
    return () => setOnSessionExpired(null);
  }, []);

  // Capture le code de parrainage (?ref=CODE) une seule fois au boot, quelle que soit la route —
  // plus robuste que de ne le lire que dans Register.tsx, au cas où le SPA navigue ailleurs avant
  // que la personne n'atteigne l'inscription (ex: /join → "Continuer sur le web" → /register).
  // Register.tsx lit ensuite oltodo_pending_referral et le nettoie après une inscription réussie.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("oltodo_pending_referral", ref);
  }, []);

  // Connexion "en tant que" un utilisateur, lancée depuis le panel admin (voir
  // OLTODO-backend/src/routes/adminStaff.ts) — un code à usage unique de 60s dans l'URL, jamais un
  // vrai jeton exposé directement. Échangé une seule fois au chargement puis retiré de l'URL
  // (jamais laissé dans l'historique du navigateur). Remplace la session courante par celle de
  // l'utilisateur ciblé, exactement comme une connexion normale.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("impersonate");
    if (!code) return;
    params.delete("impersonate");
    const cleanUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", cleanUrl);
    fetch(`${API_URL}/auth/exchange-impersonation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.accessToken && data?.user) {
          handleSetToken(data.accessToken, data.refreshToken || null);
          handleSetUser(data.user);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger le profil depuis l'API si absent, ou si en cache depuis une ancienne session
  // dont la réponse ne contenait pas encore tous les champs (ex: ville/coordonnées ajoutées
  // après coup) — sans ce re-fetch défensif, un utilisateur déjà connecté resterait bloqué
  // avec un profil incomplet jusqu'à une déconnexion manuelle.
  useEffect(() => {
    if (token && (!user || !("latitude" in user))) {
      api.get("/users/me")
        .then(data => {
          if (data?.user) {
            const flat = { ...data.user, passions: data.passions, powers: data.powers };
            setUser(flat);
            localStorage.setItem("oltodo_user", JSON.stringify(flat));
          }
        })
        .catch(() => {});
    }
  }, [token]);

  // L'access token expire au bout de 15 min côté serveur. Sans ce rafraîchissement
  // périodique, toutes les requêtes échouent silencieusement après ce délai et
  // l'app affiche des listes vides (publications, messages, etc. qui "disparaissent").
  useEffect(() => {
    if (!token) return;
    const refresh = async () => {
      const rt = refreshTokenRef.current;
      if (!rt) return;
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: rt }),
        });
        if (!res.ok) throw new Error("refresh failed");
        const data = await res.json();
        if (data.accessToken) {
          localStorage.setItem("oltodo_token", data.accessToken);
          setToken(data.accessToken);
        }
        if (data.refreshToken) {
          refreshTokenRef.current = data.refreshToken;
          localStorage.setItem("oltodo_refresh_token", data.refreshToken);
        }
      } catch {
        // Refresh token invalide ou expiré : on déconnecte proprement
        refreshTokenRef.current = null;
        localStorage.removeItem("oltodo_token");
        localStorage.removeItem("oltodo_refresh_token");
        localStorage.removeItem("oltodo_user");
        setToken(null);
        setUser(null);
      }
    };
    const interval = setInterval(refresh, 10 * 60 * 1000); // toutes les 10 min (access token valide 15 min)
    return () => clearInterval(interval);
  }, [token]);

  // Pousse la position en direct tant que le mode n'est pas 'discret'. Trois déclencheurs
  // complémentaires plutôt qu'un seul watchPosition : (1) un push immédiat au démarrage / après
  // un changement de réglage — sinon les amis ne voyaient rien jusqu'au premier mouvement ;
  // (2) watchPosition pour suivre les déplacements réels ; (3) un intervalle de secours toutes
  // les 4 min — indispensable pour une personne immobile (bureau, domicile), dont watchPosition
  // ne redéclenche jamais, et dont la position finissait donc périmée (STALE côté serveur) au
  // bout de quelques dizaines de minutes même appli ouverte.
  useEffect(() => {
    if (!token || !navigator.geolocation) return;
    let watchId: number | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;
    let lastSent = 0;
    let cancelled = false;

    const push = (lat: number, lng: number) => {
      const now = Date.now();
      if (now - lastSent < 25000) return;
      lastSent = now;
      api.put("/location/me/update", { latitude: lat, longitude: lng }).catch(() => {});
    };
    const pushCurrent = () => {
      navigator.geolocation.getCurrentPosition(
        pos => { if (!cancelled) push(pos.coords.latitude, pos.coords.longitude); },
        () => {},
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 }
      );
    };

    api.get("/location/me/settings").then(data => {
      if (cancelled || !data?.mode || data.mode === "discret") return;
      pushCurrent();
      watchId = navigator.geolocation.watchPosition(
        pos => { if (!cancelled) push(pos.coords.latitude, pos.coords.longitude); },
        () => {},
        { enableHighAccuracy: false, maximumAge: 60000 }
      );
      interval = setInterval(pushCurrent, 240000);
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (interval != null) clearInterval(interval);
    };
  }, [token, locationSharingTick]);

  const handleSetLang = (l: string) => {
    localStorage.setItem("oltodo_lang", l);
    setLang(l);
  };

  const handleSetToken = (t: string | null, newRefreshToken?: string | null) => {
    if (t) localStorage.setItem("oltodo_token", t);
    else { localStorage.removeItem("oltodo_token"); localStorage.removeItem("oltodo_user"); }
    if (newRefreshToken !== undefined) {
      refreshTokenRef.current = newRefreshToken || null;
      if (newRefreshToken) localStorage.setItem("oltodo_refresh_token", newRefreshToken);
      else localStorage.removeItem("oltodo_refresh_token");
    }
    if (!t) {
      refreshTokenRef.current = null;
      localStorage.removeItem("oltodo_refresh_token");
    }
    setToken(t);
  };

  const handleSetUser = (u: any) => {
    if (u) localStorage.setItem("oltodo_user", JSON.stringify(u));
    else localStorage.removeItem("oltodo_user");
    setUser(u);
  };

  return (
    <AppContext.Provider value={{ lang, setLang: handleSetLang, token, setToken: handleSetToken, user, setUser: handleSetUser, sideMenuOpen, setSideMenuOpen, bumpLocationSharing: () => setLocationSharingTick(t => t + 1) }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
